import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
  SecurityContext,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { debounceTime, Subject } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';
import { HX_FORM_FIELD_CONTROL, HxFormFieldControl } from '../form-group/form-field-control';
import { HxFormGroup } from '../form-group/form-group';
import { localizeSpriteHrefs } from '../icon/icon-sprite';
import { HxIconPicker } from '../icon-picker/icon-picker';
import { loadIconCategories } from '../icon-picker/icon-picker-loader';
import type { HxIconCategory } from '../icon-picker/icon-picker.models';
import { registerIconBlot } from './icon-blot';
import type {
  IQuillEditor,
  IQuillStatic,
  QuillEditorConfig,
  QuillRange,
  QuillSelectionChangeHandler,
  QuillTextChangeHandler,
} from './quill.types';

/** Format type for editor content */
export type TextEditorFormat = 'html' | 'delta';

/** Selection change event */
export interface TextEditorSelection {
  index: number;
  length: number;
}

@Component({
  selector: 'hx-text-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HxIconPicker],
  templateUrl: './text-editor.html',
  styleUrls: ['./text-editor.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: HX_FORM_FIELD_CONTROL, useExisting: HxTextEditor }],
})
// @ts-expect-error - Type mismatch by design: HxBaseControl provides 'id' as InputSignal,
// but HxFormFieldControl expects 'id' as readonly string. Access via idValue getter.
export class HxTextEditor extends HxBaseControl implements ControlValueAccessor, HxFormFieldControl<string>, OnDestroy {
  // Dependencies (inject calls)
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _sanitizer = inject(DomSanitizer);
  readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly _formGroup = inject(HxFormGroup, { optional: true });

  // ViewChild queries
  private readonly _editorContainer = viewChild.required<ElementRef<HTMLDivElement>>('editorContainer');
  private readonly _iconPicker = viewChild<HxIconPicker>('iconPicker');

  // State signals
  // Context-aware: knows if it's inside a form-group container
  protected readonly _isInContainer = !!this._formGroup;

  // --- Icon Categories ---
  protected readonly iconCategories = signal<HxIconCategory[]>([]);

  // --- Quill Instance ---
  private _quill: IQuillEditor | null = null;
  private readonly _quillReady = signal(false);
  private readonly _contentChangeSubject = new Subject<void>();
  private _pendingValueApplied = false; // Track if initial value was applied to Quill

  // --- Event Listener Storage (for proper cleanup) ---
  private _textChangeListener: QuillTextChangeHandler | null = null;
  private _selectionChangeListener: QuillSelectionChangeHandler | null = null;
  private _shiftEnterListener: ((event: KeyboardEvent) => void) | null = null;
  private _pasteListener: ((event: ClipboardEvent) => void) | null = null;

  // --- Component Inputs ---

  /**
   * Controls the editor height.
   *
   * Sets the size of the text editor container.
   *
   * - `'sm'`: Small (compact height)
   * - `'md'`: Medium (default)
   * - `'lg'`: Large (increased height)
   * - `'xl'`: Extra large (maximum height)
   *
   * @default 'md'
   */
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('md');

  /**
   * Content format for value input/output.
   *
   * Determines how the editor content is serialized.
   *
   * - `'html'`: Content as HTML string (default, readable format)
   * - `'delta'`: Content as Quill Delta object (preserves formatting metadata)
   *
   * @default 'html'
   */
  readonly format = input<TextEditorFormat>('html');

  /**
   * Debounce time for value change emissions in milliseconds.
   *
   * Delays the `valueChange` output to reduce frequency of updates during rapid typing. Higher values reduce update
   * frequency but increase perceived latency.
   *
   * @default 300
   */
  readonly debounceTime = input<number>(300);

  // --- Component Outputs ---

  /** Emitted when editor value changes (debounced) */
  readonly valueChange = output<string>();

  /** Emitted when editor gains focus */
  readonly focusEvent = output<void>();

  /** Emitted when editor loses focus */
  readonly blurEvent = output<void>();

  /** Emitted when selection changes */
  readonly selectionChange = output<TextEditorSelection>();

  // --- HxFormFieldControl Implementation ---

  /** Unique identifier generated for this editor (fallback if no @Input id provided) */
  private readonly _generatedId = `hx-text-editor-${Math.random().toString(36).substring(2, 9)}`;

  /** Gets the id value for use in template and interface methods */
  get idValue(): string {
    return this.getIdValue() || this._generatedId;
  }

  /** Stream that emits whenever the state of the control changes */
  readonly stateChanges = new Subject<void>();

  /** The type of control (for HxFormGroup) */
  readonly formFieldControlType = 'hx-text-editor';

  /** Whether the editor is currently focused */
  focused = false;

  /** Current content stored internally */
  private readonly _internalValue = signal<string>('');

  // --- Toolbar Configuration (Fixed) ---
  private readonly _toolbarConfig = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'icon'],
    ['clean'],
  ];

  // --- HxFormFieldControl Properties (as getters) ---

  /** Gets the current value from Quill */
  get value(): string {
    return this._internalValue();
  }

  /** Sets the current value on Quill */
  set value(val: string) {
    const newValue = val ?? '';
    if (this._internalValue() !== newValue) {
      this._internalValue.set(newValue);
      this._updateQuillContent(newValue);
      this.stateChanges.next();
    }
  }

  /** Gets the placeholder value for internal use */
  get placeholderValue(): string {
    return this.placeholder();
  }

  /** Gets the disabled state for HxFormFieldControl interface */
  get disabledValue(): boolean {
    return this.disabledState();
  }

  /** Gets the required state for HxFormFieldControl interface */
  get requiredValue(): boolean {
    return this.required();
  }

  /** Whether the editor is empty */
  get empty(): boolean {
    if (!this._quillReady()) return true;
    const text = this._quill?.getText()?.trim() || '';
    return text.length === 0;
  }

  /** Whether the control is in an error state */
  get errorState(): boolean {
    const control = this.ngControl?.control;
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /** Whether the label should float */
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  constructor() {
    super();
    // Register this component as the CVA implementation
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Load all icon categories from manifest.json
    loadIconCategories().then((categories) => {
      this.iconCategories.set(categories);
    });

    // Setup debounced content change handler
    this._contentChangeSubject
      .pipe(debounceTime(this.debounceTime()), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._emitValueChange();
      });

    // Initialize Quill after render (SSR-safe)
    afterNextRender(() => {
      if (isPlatformBrowser(this._platformId)) {
        this._initializeQuill();
      }
    });

    // Watch for disabled state changes
    effect(() => {
      const disabled = this.disabledState();
      if (this._quill && this._quillReady()) {
        this._quill.enable(!disabled);
      }
    });

    // Watch for readonly state changes
    effect(() => {
      const readOnly = this.readonly();
      if (this._quill && this._quillReady()) {
        this._quill.enable(!readOnly && !this.disabledState());
      }
    });

    // Watch for when Quill becomes ready and apply any pending value
    // This handles the race condition where writeValue is called before Quill initializes
    effect(() => {
      const ready = this._quillReady();
      const pendingValue = this._internalValue();
      if (ready && this._quill && pendingValue && !this._pendingValueApplied) {
        this._pendingValueApplied = true;
        this._updateQuillContent(pendingValue);
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up RxJS subjects
    this._contentChangeSubject.complete();
    this.stateChanges.complete();

    // Clean up Quill instance and event listeners
    if (this._quill && this._quill.root) {
      // Remove event listeners if they were attached
      if (this._textChangeListener) {
        this._quill.off('text-change', this._textChangeListener);
        this._textChangeListener = null;
      }

      if (this._selectionChangeListener) {
        this._quill.off('selection-change', this._selectionChangeListener);
        this._selectionChangeListener = null;
      }
    }

    if (this._shiftEnterListener) {
      this._quill.root.removeEventListener('keydown', this._shiftEnterListener, true);
      this._shiftEnterListener = null;
    }

    if (this._pasteListener) {
      this._quill.root.removeEventListener('paste', this._pasteListener, true);
      this._pasteListener = null;
    }

    // Clear Quill instance reference
    this._quill = null;
  }

  // --- Quill Initialization ---

  private async _initializeQuill(): Promise<void> {
    try {
      // Dynamic import of Quill (Parchment is exported from Quill)
      const QuillModule = await import('quill');
      const quillStatic = QuillModule.default as unknown as IQuillStatic;
      const Parchment = (QuillModule as any).Parchment || (quillStatic as any).import('parchment');

      // Register custom text case attributor
      const CaseAttributor = new Parchment.StyleAttributor('case', 'text-transform', {
        scope: Parchment.Scope.INLINE,
        whitelist: ['uppercase', 'lowercase', 'capitalize', 'none'],
      });
      quillStatic.register(CaseAttributor, true);

      // Register custom icon blot for inline icon insertion
      registerIconBlot(quillStatic);

      // Register custom toolbar icon for the 'icon' button (star-fill icon)
      const icons = quillStatic.import('ui/icons');
      icons['icon'] =
        '<svg viewBox="0 0 18 18"><path class="ql-fill" d="M9 2l2.5 5.5L17 8.3l-4 4 1 5.7L9 15.5 3.9 18l1-5.7-4-4 5.6-.8z"/></svg>';

      // Create Quill instance with proper config typing
      const quillConfig: QuillEditorConfig = {
        theme: 'snow',
        placeholder: this.placeholderValue,
        readOnly: this.readonly() || this.disabledState(),
        modules: {
          toolbar: {
            container: this._toolbarConfig,
            handlers: {
              case: function (this: any, value: string | boolean) {
                if (value) {
                  this.quill.format('case', value);
                } else {
                  this.quill.format('case', false);
                }
              },
              icon: () => {
                // Open icon picker dialog
                this._iconPicker()?.openPicker();
              },
            },
          },
          clipboard: {
            matchVisual: false, // Preserve formatting on paste
          },
        },
      };

      this._quill = new quillStatic(this._editorContainer().nativeElement, quillConfig);

      // Shift+Enter in lists: insert a soft line break (<br>) within the same list item.
      // Uses the browser's native insertLineBreak command — this inserts a <br> inside the
      // <li> element without touching Quill's delta model or list block structure.
      // Regular Enter continues to create new list items as Quill's default behavior.
      const quillRef = this._quill as any;
      this._shiftEnterListener = (event: KeyboardEvent) => {
        if (!event.shiftKey || event.key !== 'Enter') return;

        // DOM-based list detection — always in sync, even right after toolbar click
        const nativeSel = document.getSelection();
        const anchor = nativeSel?.anchorNode;
        if (!anchor) return;
        const el = anchor.nodeType === Node.ELEMENT_NODE ? (anchor as Element) : anchor.parentElement;
        if (!el?.closest('li')) return;

        // Block the event before Quill or the browser can process it
        event.preventDefault();
        event.stopImmediatePropagation();

        // Native browser line break — inserts <br> within the current <li>
        // Keeps list structure intact, no Quill API corruption
        document.execCommand('insertLineBreak');
        quillRef.update('user');
      };
      this._quill.root.addEventListener('keydown', this._shiftEnterListener, true);

      // Set initial content if available (value may have been set via writeValue before Quill init)
      if (this._internalValue()) {
        const initialValue = this._internalValue();
        const currentFormat = this.format();

        if (currentFormat === 'html') {
          // Set HTML directly into Quill DOM to preserve Quill-specific attributes
          // (data-list, ql-indent, etc.) that Angular's DomSanitizer would strip.
          this._quill.root.innerHTML = localizeSpriteHrefs(initialValue || '');
          (this._quill as any).update('silent');
        } else if (currentFormat === 'delta') {
          try {
            const delta = JSON.parse(initialValue || '{"ops":[]}');
            this._quill.setContents(delta, 'silent');
          } catch (parseError) {
            console.error('Invalid initial delta format:', parseError);
          }
        }
        // Mark as applied so the effect doesn't double-apply
        this._pendingValueApplied = true;
      }

      // Attach event listeners with stored references for cleanup
      this._textChangeListener = () => {
        this._handleContentChange();
      };
      this._quill.on('text-change', this._textChangeListener);

      this._selectionChangeListener = (range: QuillRange | null) => {
        if (range) {
          // Editor gained focus
          if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
            this.focusEvent.emit();
          }
          this.selectionChange.emit({ index: range.index, length: range.length });
        } else {
          // Editor lost focus
          if (this.focused) {
            this.focused = false;
            this.stateChanges.next();
            this.markTouched();
            this.blurEvent.emit();
          }
        }
      };
      this._quill.on('selection-change', this._selectionChangeListener);

      // Sanitize HTML paste once at the clipboard boundary to preserve formatting.
      // Avoids recursive clipboard conversion in Quill matchers.
      this._pasteListener = (event: ClipboardEvent) => {
        if (!this._quill) return;

        const html = event.clipboardData?.getData('text/html') || '';
        if (!html) return; // Allow default for plain text paste.

        const sanitized = this._sanitizer.sanitize(SecurityContext.HTML, html);
        if (!sanitized) return; // Allow default if sanitization strips everything.

        event.preventDefault();
        event.stopPropagation();

        const range = this._quill.getSelection(true);
        const index = range?.index ?? this._quill.getLength();
        this._quill.clipboard.dangerouslyPasteHTML(index, sanitized, 'user');
      };
      this._quill.root.addEventListener('paste', this._pasteListener, true);

      this._quillReady.set(true);
      // Mark pending value as applied to prevent the effect from firing
      // when user types the first character in an empty editor
      this._pendingValueApplied = true;
      this.stateChanges.next();
    } catch (error) {
      console.error('Failed to initialize Quill editor:', error);
      this._quillReady.set(false);
    }
  }

  // --- Icon Picker Integration ---

  /** Handles icon selection from icon picker and inserts at cursor position */
  protected onIconSelected(iconName: string): void {
    if (!this._quill || !this._quillReady() || !iconName) return;

    // Get current cursor position (or end of document if no selection)
    const range = this._quill.getSelection() || { index: this._quill.getLength(), length: 0 };

    try {
      // Insert icon at cursor position
      this._quill.insertEmbed(range.index, 'icon', iconName, 'user');
      // Move cursor after the inserted icon
      this._quill.setSelection(range.index + 1, 0);
      // Focus back to editor
      this._quill.focus();
    } catch (error) {
      console.error('Failed to insert icon:', error);
    }
  }

  // --- Content Management ---

  private _updateQuillContent(value: string): void {
    if (!this._quill || !this._quillReady()) return;

    const currentFormat = this.format();

    try {
      if (currentFormat === 'html') {
        // Set HTML directly into Quill DOM to preserve Quill-specific attributes
        // (data-list, ql-indent, etc.) that Angular's DomSanitizer would strip.
        this._quill.root.innerHTML = localizeSpriteHrefs(value || '');
        (this._quill as any).update('silent');
      } else if (currentFormat === 'delta') {
        try {
          const delta = JSON.parse(value || '{"ops":[]}');
          this._quill.setContents(delta, 'silent');
        } catch (parseError) {
          console.error('Invalid delta format:', parseError);
          this._quill.setText('');
        }
      }
    } catch (error) {
      console.error('Error updating Quill content:', error);
    }
  }

  private _getQuillContent(): string {
    if (!this._quill || !this._quillReady()) {
      return '';
    }

    try {
      const currentFormat = this.format();

      if (currentFormat === 'html') {
        // Use root.innerHTML directly to preserve DOM-level content like <br> soft
        // line breaks (from Shift+Enter) that getSemanticHTML() loses because it
        // regenerates from the delta model. innerHTML also retains Quill-specific
        // attributes (data-list, ql-indent, ql-align) needed for display rendering.
        return this._quill.root.innerHTML || '';
      } else {
        const delta = this._quill.getContents();
        return JSON.stringify(delta);
      }
    } catch (error) {
      console.error('Error retrieving Quill content:', error);
      return '';
    }
  }

  private _handleContentChange(): void {
    const newValue = this._getQuillContent();
    this._internalValue.set(newValue);
    this.notifyChange(newValue);
    this.stateChanges.next();

    // Trigger debounced output emission
    this._contentChangeSubject.next();
  }

  private _emitValueChange(): void {
    this.valueChange.emit(this._internalValue());
  }

  // --- HxFormFieldControl Methods ---

  setDescribedByIds(ids: string[]): void {
    const element = this._editorContainer().nativeElement;
    if (ids.length > 0) {
      element.setAttribute('aria-describedby', ids.join(' '));
    } else {
      element.removeAttribute('aria-describedby');
    }
  }

  onContainerClick(_event: MouseEvent): void {
    if (this._quill) {
      this._quill.focus();
    }
  }

  focus(): void {
    if (this._quill) {
      this._quill.focus();
    }
  }

  // --- HxBaseControl Overrides ---

  /** Sets the value from form model (ControlValueAccessor) */
  protected _setValue(value: string): void {
    this.value = value;
  }

  /** Validates the editor value */
  protected _validate(_control: AbstractControl): ValidationErrors | null {
    // Validation is handled by HxFormFieldControl errorState
    return null;
  }
}
